/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { AxiosError } from 'axios';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AirmetFromBackend, SigmetFromBackend } from '@opengeoweb/api';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import {
  AlertBanner,
  dateUtils,
  getAxiosErrorMessage,
  useConfirmationDialog,
} from '@opengeoweb/shared';
import type {
  Airmet,
  AirmetFromFrontend,
  AviationProduct,
  CancelAirmet,
  CancelSigmet,
  ProductConfig,
  ProductStatus,
  Sigmet,
  SigmetConfig,
  SigmetFromFrontend,
} from '@opengeoweb/sigmet-airmet';
import {
  createAirmetPostParameter,
  createSigmetPostParameter,
  getConfirmationDialogButtonLabel,
  getConfirmationDialogTitle,
  isVAPhenomenon,
  prepareCancelSigmet,
  ProductAction,
} from '@opengeoweb/sigmet-airmet';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { uiActions } from '@opengeoweb/store';
import type { ConfirmDialogProps } from './ConfirmWarningDialog';
import { ConfirmDeleteWarningDialog } from './ConfirmWarningDialog';
import { PublicWarningList } from './PublicWarningList';
import { getBatchDeleteToggleIds } from './SelectAllButton';
import { useWarningListFeatureSync } from './useWarningListFeatureSync';
import { useAutoRefetchOnExpiry, useWarnings } from '../../api/api-utils';
import {
  usePostAirmet,
  usePostSigmet,
  useProductConfig,
} from '../../api/aviation-api/hooks';
import {
  useFetchPublicWarning,
  useMutateDeleteWarning,
  useMutateDeleteWarningEvent,
  useMutateMaritimeWarningsEditor,
  useMutatePublicWarningEditor,
  usePostMaritimeWarnings,
  useSaveWarning,
} from '../../api/warning-api/hooks';
import type { WarningModuleStore } from '../../store/types';
import { getWarningsEventIsFormDirty } from '../../store/warningsEventForm/selectors';
import { warningsFiltersActions } from '../../store/warningsFilters/reducer';
import {
  getExpandedSectionsByPanelId,
  getHiddenWarningIdsByPanelId,
  getWarningListActionRequestByPanelId,
  getWarningsFilterById,
} from '../../store/warningsFilters/selectors';
import type { ExpandedWarningSections } from '../../store/warningsFilters/types';
import type { FormAction } from '../../store/warningsForm/reducer';
import { warningsFormActions } from '../../store/warningsForm/reducer';
import {
  getPublicWarning,
  getSelectedPublicIsFormDirty,
  getSelectedWarningId,
  getWarningType,
} from '../../store/warningsForm/selectors';
import type {
  AirmetWarning,
  MaritimeWarning,
  MaritimeWarningsType,
  SigmetWarning,
  Warning,
  WarningType,
} from '../../store/warningsForm/types';
import {
  MaritimeWarningAction,
  PublicWarningGwType,
  PublicWarningStatus,
} from '../../store/warningsForm/types';
import {
  publicWarningDialogType,
  warningEventDialogType,
} from '../../store/warningsForm/utils';
import { MAX_WARNINGS_DISPLAY } from '../../utils/constants';
import { useWarningsTranslation, WARN_NAMESPACE } from '../../utils/i18n';
import {
  amendAviationProduct,
  duplicateAviationProduct,
  renewAviationProduct,
} from '../../utils/sig-airmet-util';
import {
  getWarningWithLinkedToId,
  isMaritimeWarningType,
  isPublicUpdateDraft,
} from '../../utils/warningUtils';
import type {
  FilterState,
  PickedFilterState,
} from '../FilterList/filter-hooks';
import { useWarningFilters } from '../FilterList/filter-hooks';
import { TimeFilterMode } from '../FilterList/filter-utils';
import { CancelAviationConfirmDialog } from '../PublicWarningsFormDialog/CancelAviationConfirmDialog';
import { warningFormConfirmationOptions } from '../PublicWarningsFormDialog/PublicWarningsFormDialogConnect';
import {
  useCloseWarningEventDialogOnUnmount,
  useWarningEventActions,
} from '../WarningEvents/WarningEventDialog';

export interface PublicWarningListConnectProps {
  panelId: string;
  presetFilterState?: PickedFilterState;
  presetExpandedSections?: ExpandedWarningSections;
  onPreventCloseView?: (shouldPreventClose: boolean) => void;
}

export const PublicWarningListConnect: React.FC<
  PublicWarningListConnectProps
> = ({
  panelId,
  presetExpandedSections,
  presetFilterState,
  onPreventCloseView,
}) => {
  const [selectedForBatchDelete, setSelectedForBatchDelete] = React.useState<
    string[]
  >([]);

  const selectAllForBatchDelete = React.useCallback(
    (warningIds: string[]): void => {
      setSelectedForBatchDelete(Array.from(new Set(warningIds)));
    },
    [],
  );

  const clearBatchDeleteSelection = React.useCallback((): void => {
    setSelectedForBatchDelete([]);
  }, []);

  const updatePublicWarningEditor = useMutatePublicWarningEditor();
  const { mutateAsync: updateMaritimeWarningEditor } =
    useMutateMaritimeWarningsEditor();
  const { mutateAsync: deletePublicWarning } = useMutateDeleteWarning();
  const fetchPublicWarning = useFetchPublicWarning();
  const { mutateAsync: deleteWarningEvent } = useMutateDeleteWarningEvent();
  const { mutateAsync: saveWarning } = useSaveWarning();
  const { mutateAsync: postMaritimeWarnings } = usePostMaritimeWarnings();
  const { t } = useWarningsTranslation();
  const dispatch = useDispatch();
  const confirmDialog = useConfirmationDialog();
  const { auth, isLoggedIn } = useAuthenticationContext();

  const savedFilterState = useSelector((store: WarningModuleStore) =>
    getWarningsFilterById(store, panelId),
  );

  const {
    combinedWarnings,
    refetchWarnings,
    isFetching,
    error,
    dataUpdatedAt,
  } = useWarnings(false, savedFilterState.time);

  useAutoRefetchOnExpiry(combinedWarnings, refetchWarnings);

  const lastUpdateTime = dateUtils.dateToString(
    new Date(dataUpdatedAt),
    dateUtils.DATE_FORMAT_HOURS,
  );
  const [confirmDialogOptions, setConfirmDialogOptions] = React.useState<
    ConfirmDialogProps | undefined
  >(undefined);
  const selectedWarningId = useSelector(getSelectedWarningId);
  const selectedFormWarning = useSelector(getPublicWarning) as
    | Warning
    | undefined;

  const isFormDirty = useSelector(getSelectedPublicIsFormDirty);
  const isEventFormDirty = useSelector(getWarningsEventIsFormDirty);

  useCloseWarningEventDialogOnUnmount(panelId);

  React.useEffect(() => {
    onPreventCloseView?.(isEventFormDirty);
  }, [isEventFormDirty, onPreventCloseView]);

  const openPublicWarningDialog = async (
    formAction?: FormAction,
    warning?: Warning,
    warningType?: WarningType,
  ): Promise<void> => {
    if (isFormDirty) {
      const mayProceed = await confirmDialog(warningFormConfirmationOptions(t))
        .then(() => true)
        .catch(() => false);

      if (!mayProceed) {
        return;
      }
    }

    dispatch(
      warningsFormActions.openPublicWarningFormDialog({
        ...(warning && {
          warning,
        }),
        formState: formAction,
        warningType,
        panelId,
      }),
    );
  };

  const {
    selectedEventId,
    openWarningsEventDialog,
    deleteWarningsEvent,
    updateWarningsEvent,
  } = useWarningEventActions(panelId, savedFilterState.time);

  const selectWarning = (warning?: Warning): void => {
    if (isMaritimeWarningType(warning?.type)) {
      handleMaritimeProductClick(
        warning as MaritimeWarning,
        MaritimeWarningAction.SELECT,
      );
    } else if (warning?.type === 'warningsEvent') {
      void openWarningsEventDialog('readonly', warning);
    } else {
      void openPublicWarningDialog('readonly', warning);
    }
  };
  const createWarning = (warningType?: WarningType): void => {
    if (warningType === 'warningsEvent') {
      void openWarningsEventDialog('edit');
      return;
    }
    void openPublicWarningDialog('', undefined, warningType);
  };

  // The unpublished update draft of a published warning, if it has one. The
  // draft is not in the list when a custom time filter leaves it out.
  const getOpenUpdateDraft = async (
    warning: Warning,
  ): Promise<Warning | undefined> => {
    if (warning.type !== 'public' || !warning.next_id) {
      return undefined;
    }
    const draftId = warning.next_id;
    const draft =
      combinedWarnings.find(({ id }) => id === draftId) ??
      (await fetchPublicWarning(draftId).catch((fetchError) => {
        openSnackbar(getAxiosErrorMessage(fetchError as AxiosError));
        return undefined;
      }));
    return isPublicUpdateDraft(draft) ? draft : undefined;
  };

  // The warning points to a successor that is not an open update draft: the
  // draft could not be fetched, or the list is out of date. Acting on it now
  // would ignore that successor, so refresh the list and let the user retry.
  const isStaleWarning = (warning: Warning, draft?: Warning): boolean => {
    if (draft || warning.type !== 'public' || !warning.next_id) {
      return false;
    }
    refetchWarnings();
    openSnackbar(t('warning-list-stale-warning'));
    return true;
  };

  const editWarning = async (warning: Warning): Promise<void> => {
    if (warning.type === 'warningsEvent') {
      await updateWarningsEvent(warning.eventId!, auth?.username || '');
      return;
    }
    if (warning.status === PublicWarningStatus.PUBLISHED) {
      // A published warning can only have one open update draft, so creating
      // a second one fails. Open the existing draft instead.
      const updateDraft = await getOpenUpdateDraft(warning);
      if (updateDraft) {
        await editWarning(updateDraft);
        return;
      }
      if (isStaleWarning(warning, updateDraft)) {
        return;
      }
    }
    const newWarning =
      warning.status === PublicWarningStatus.PUBLISHED
        ? getWarningWithLinkedToId(warning)
        : warning;
    if (newWarning.id && auth?.username && !newWarning.editor) {
      refetchWarnings();
      updatePublicWarningEditor.mutate({
        id: newWarning.id,
        isEditor: true,
      });

      await openPublicWarningDialog('', {
        ...newWarning,
        editor: auth?.username,
      });
    } else {
      void openPublicWarningDialog('', newWarning);
    }
  };

  // An update draft can no longer be published once its warning is expired or
  // withdrawn, so the backend deletes it as part of the expire/withdraw. The
  // confirm dialog tells the user beforehand. Undefined when the warning is
  // stale, as the notice could then be missing a draft.
  const getUpdateDraftNotice = async (
    warning: Warning,
  ): Promise<{ draftId?: string; draftMessage: string } | undefined> => {
    const draft = await getOpenUpdateDraft(warning);
    if (isStaleWarning(warning, draft)) {
      return undefined;
    }
    if (!draft?.id) {
      return { draftMessage: '' };
    }
    const message = draft.editor
      ? t('warning-dialog-discard-update-draft-editor', {
          EDITOR: draft.editor,
        })
      : t('warning-dialog-discard-update-draft');
    return { draftId: draft.id, draftMessage: ` ${message}` };
  };

  // The draft is gone after the expire/withdraw, so close its form if open.
  const closeDeletedDraftForm = (draftId?: string): void => {
    if (draftId && selectedWarningId === draftId) {
      dispatch(
        uiActions.setToggleOpenDialog({
          setOpen: false,
          type: publicWarningDialogType,
        }),
      );
    }
  };

  const openExpireDialog = async (warning: Warning): Promise<void> => {
    const expiredWarning: Warning = {
      ...warning,
      status: PublicWarningStatus.EXPIRED,
      ...(warning.type === 'public' && { gw_type: PublicWarningGwType.EXPIRE }),
    };
    const notice = await getUpdateDraftNotice(warning);
    if (!notice) {
      return;
    }
    const { draftId, draftMessage } = notice;

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-expire-confirm'),
      title: t('warning-dialog-expire-title'),
      description: `${t('warning-dialog-expire-description')}${draftMessage}`,
      request: async () =>
        saveWarning({
          id: expiredWarning.id!,
          data: { ...expiredWarning, type: 'public' },
        }),
      callback: () => {
        closeDeletedDraftForm(draftId);
        onRemoveSuccess(expiredWarning.id!, t('warning-dialog-expire-succes'));
      },
    });
  };

  const expireWarning = (warning: Warning): void => {
    void openExpireDialog(warning);
  };

  const openWithdrawDialog = async (warning: Warning): Promise<void> => {
    const withdrawnWarning: Warning = {
      ...warning,
      status: PublicWarningStatus.WITHDRAWN,
      ...(warning.type === 'public' && {
        gw_type: PublicWarningGwType.WITHDRAW,
      }),
    };
    const notice = await getUpdateDraftNotice(warning);
    if (!notice) {
      return;
    }
    const { draftId, draftMessage } = notice;

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-withdraw-confirm'),
      title: t('warning-dialog-withdraw-title'),
      description: `${t('warning-dialog-withdraw-description')}${draftMessage}`,
      request: async () =>
        saveWarning({
          id: withdrawnWarning.id!,
          data: { ...withdrawnWarning, type: 'public' },
        }),
      callback: () => {
        closeDeletedDraftForm(draftId);
        onRemoveSuccess(
          withdrawnWarning.id!,
          t('warning-dialog-withdraw-succes'),
        );
      },
    });
  };

  const withdrawWarning = (warning: Warning): void => {
    void openWithdrawDialog(warning);
  };

  const warningType = useSelector(getWarningType);

  const { data: aviationConfig } = useProductConfig(warningType, false);
  const { mutateAsync: postSigmet } = usePostSigmet();
  const { mutateAsync: postAirmet } = usePostAirmet();

  const postAviationProduct = async (
    productToPost: AviationProduct,
    newStatus: ProductStatus,
    type: 'sigmet' | 'airmet',
    opts?: { vaSigmetMoveToFIR?: string },
  ): Promise<SigmetFromBackend | AirmetFromBackend> => {
    const uuid: string | null = productToPost?.uuid ?? null;

    const body =
      newStatus === 'CANCELLED'
        ? getCancelSigmet(productToPost, type, opts?.vaSigmetMoveToFIR)
        : productToPost;

    if (type === 'sigmet') {
      const param: SigmetFromFrontend = createSigmetPostParameter(
        body as Sigmet | CancelSigmet,
        newStatus,
        uuid!,
      );
      return postSigmet(param);
    }
    const param: AirmetFromFrontend = createAirmetPostParameter(
      body as Airmet | CancelAirmet,
      newStatus,
      uuid!,
    );
    return postAirmet(param);
  };

  const getCancelSigmet = (
    selectedProduct: AviationProduct,
    type: 'sigmet' | 'airmet',
    vaSigmetMoveToFIR?: string,
  ): Sigmet | Airmet => {
    if (type === 'sigmet') {
      if (!vaSigmetMoveToFIR || vaSigmetMoveToFIR === 'UNKNOWN') {
        return selectedProduct as unknown as Sigmet;
      }
      return prepareCancelSigmet(
        selectedProduct as unknown as Sigmet,
        vaSigmetMoveToFIR,
      );
    }
    return selectedProduct as unknown as Airmet;
  };

  const cancelAviationProduct = async (
    warning: AviationProduct,
    type: 'sigmet' | 'airmet',
    opts?: { vaSigmetMoveToFIR?: string },
  ): Promise<SigmetFromBackend | AirmetFromBackend | null> => {
    const result = await postAviationProduct(warning, 'CANCELLED', type, opts);
    return result ?? null;
  };

  const hiddenWarningIdsArray = useSelector((store: WarningModuleStore) =>
    getHiddenWarningIdsByPanelId(store, panelId),
  );
  const warningListActionRequest = useSelector((store: WarningModuleStore) =>
    getWarningListActionRequestByPanelId(store, panelId),
  );

  const hiddenWarningIds = React.useMemo(
    () => new Set(hiddenWarningIdsArray),
    [hiddenWarningIdsArray],
  );

  const handleToggleWarningVisibility = React.useCallback(
    (warningId: string): void => {
      const next = hiddenWarningIdsArray.includes(warningId)
        ? hiddenWarningIdsArray.filter((id) => id !== warningId)
        : [...hiddenWarningIdsArray, warningId];

      dispatch(
        warningsFiltersActions.setHiddenWarningIdsForPanel({
          panelId,
          warningIds: next,
        }),
      );
    },
    [dispatch, hiddenWarningIdsArray, panelId],
  );

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelCtx, setCancelCtx] = React.useState<{
    type: 'sigmet' | 'airmet';
    product: AviationProduct;
    showVA: boolean;
    sigmet?: Sigmet;
  } | null>(null);

  const openCancelDialog = (warning: SigmetWarning | AirmetWarning): void => {
    const type = warning.warningDetail.level === 'SIG' ? 'sigmet' : 'airmet';
    const product = warning.sigmetAirmetDetails as AviationProduct;
    const showVA = isVAPhenomenon(
      warning.sigmetAirmetDetails as unknown as Sigmet,
      ProductAction.CANCEL,
    );
    setCancelCtx({
      type,
      product,
      showVA,
      sigmet: warning.sigmetAirmetDetails as unknown as Sigmet,
    });
    setCancelOpen(true);
  };

  const closeCancelDialog = (): void => {
    setCancelOpen(false);
    setCancelCtx(null);
  };

  const handleAviationProductClick = (
    warning?: SigmetWarning | AirmetWarning,
    actionType?: ProductAction,
  ): void => {
    if (!warning) {
      return;
    }

    const type = warning.warningDetail.level === 'SIG' ? 'sigmet' : 'airmet';

    let updatedWarning: SigmetWarning | AirmetWarning | undefined;

    switch (actionType) {
      case ProductAction.AMEND:
        void openPublicWarningDialog(
          'amending',
          amendAviationProduct(warning, aviationConfig as ProductConfig),
          type,
        );
        break;
      case 'RENEW':
        updatedWarning = renewAviationProduct(
          warning,
          aviationConfig as ProductConfig,
        );
        void openPublicWarningDialog('edit', updatedWarning, type);
        break;
      case 'DUPLICATE':
        updatedWarning = duplicateAviationProduct(warning);
        void openPublicWarningDialog('edit', updatedWarning, type);
        break;
      case ProductAction.CANCEL: {
        openCancelDialog(warning);
        break;
      }
      case 'DISCARD': {
        const editor = warning.editor ?? '';
        const currentUser = auth?.username ?? '';

        const isLocked = editor !== '' && editor !== currentUser;

        setConfirmDialogOptions({
          confirmLabel: t('warning-dialog-delete-confirm'),
          title: t('warning-dialog-delete-title'),
          description: t('warning-dialog-delete-description'),
          request: async () =>
            postAviationProduct(
              warning.sigmetAirmetDetails as AviationProduct,
              'DISCARDED',
              type,
            ),
          callback: () => onRemoveSuccess(warning.id!),
          isLocked,
        });
        break;
      }

      default:
        break;
    }
  };

  const editMaritimeWarning = async (
    warning: MaritimeWarning,
  ): Promise<void> => {
    if (warning && auth?.username && !warning.editor) {
      try {
        await updateMaritimeWarningEditor({
          domain: warning.type,
          isEditor: true,
        });
      } catch {
        /* do nothing, refetching the list will update the form */
      }
      refetchWarnings();
    }
    void openPublicWarningDialog('', undefined, warning.type);
  };

  const extendMaritimeWarning = (warningToExtend: Warning): void => {
    const allMaritimeWarningsForType =
      (combinedWarnings?.filter(
        (warning) =>
          warning.type === warningToExtend.type &&
          warning.status === 'PUBLISHED',
      ) as MaritimeWarning[]) || ([] as MaritimeWarning[]);

    const now = Date.now();

    // Construct new warnings
    const extendedWarnings = allMaritimeWarningsForType.map((warning) => ({
      type: warning.type,
      status: PublicWarningStatus.PUBLISHED,
      editor: auth?.username ?? '',
      warningDetail: {
        phenomenon: 'wind',
        validFrom: warning.warningDetail.validFrom, // keep original validFrom when extending
        validUntil: new Date(now + 24 * 60 * 60 * 1000).toISOString(),
        level: warning.warningDetail.level,
        probability: 100,
        areas: warning.warningDetail.areas,
        windDirectionFrom: warning.warningDetail.windDirectionFrom,
        windForce: warning.warningDetail.windForce,
        changeType: warning.warningDetail.changeType,
        windDirectionTo: warning.warningDetail.windDirectionTo,
        descriptionOriginal: warning.warningDetail.descriptionOriginal,
        descriptionTranslation: warning.warningDetail.descriptionTranslation,
        domain: warning.type,
      },
    }));
    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-extend-confirm'),
      title: t('warning-dialog-extend-title'),
      description: t('warning-dialog-extend-description'),
      request: async () => {
        if (
          warningToExtend &&
          auth?.username &&
          warningToExtend.editor !== auth.username
        ) {
          await updateMaritimeWarningEditor({
            domain: warningToExtend.type as MaritimeWarningsType,
            isEditor: true,
          });
        }
        await postMaritimeWarnings(extendedWarnings);
      },
      callback: () =>
        onRemoveSuccess(warningToExtend.id!, t('warning-dialog-extend-succes')),
    });
    void refetchWarnings();
  };

  const handleMaritimeProductClick = (
    warning?: MaritimeWarning,
    actionType?: MaritimeWarningAction,
  ): void => {
    if (!warning) {
      return;
    }
    switch (actionType) {
      case MaritimeWarningAction.SELECT:
        void openPublicWarningDialog('', undefined, warning.type);
        break;
      case MaritimeWarningAction.UPDATE:
        void editMaritimeWarning(warning);
        break;
      case MaritimeWarningAction.EXTEND:
        extendMaritimeWarning(warning);
        break;
      default:
        break;
    }
  };

  const openSnackbar = (message: string): void => {
    dispatch(
      snackbarActions.openSnackbar({
        type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
        message,
      }),
    );
  };

  const deleteWarning = (warning: Warning): void => {
    const editor = warning.editor ?? '';
    const currentUser = auth?.username ?? '';

    const isLocked = editor !== '' && editor !== currentUser;

    if (warning.type === 'warningsEvent') {
      void deleteWarningsEvent(
        warning.eventId!,
        currentUser,
        setConfirmDialogOptions,
      );
      return;
    }

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-delete-confirm'),
      title: t('warning-dialog-delete-title'),
      description: t('warning-dialog-delete-description'),
      request: async () =>
        deletePublicWarning(
          { id: warning.id! },
          { onSuccess: () => onRemoveSuccessHooks(warning.id!) },
        ),
      isLocked,
    });
  };

  const deleteReviewWarning = (warning: Warning): void => {
    const ignoredWarning: Warning = {
      ...warning,
      status: PublicWarningStatus.IGNORED,
    };

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-delete-confirm'),
      title: t('warning-dialog-delete-title'),
      description: t('warning-dialog-delete-description'),
      request: async () =>
        saveWarning({
          id: ignoredWarning.id!,
          data: { ...ignoredWarning, type: 'public' },
        }),
      callback: () =>
        onRemoveSuccess(ignoredWarning.id!, t('warning-dialog-delete-succes')),
    });
    void refetchWarnings();
  };

  const warningListActionHandlersRef = React.useRef({
    deleteReviewWarning,
    deleteWarning,
    editWarning,
    expireWarning,
    handleAviationProductClick,
    handleMaritimeProductClick,
    withdrawWarning,
  });
  warningListActionHandlersRef.current = {
    deleteReviewWarning,
    deleteWarning,
    editWarning,
    expireWarning,
    handleAviationProductClick,
    handleMaritimeProductClick,
    withdrawWarning,
  };

  React.useEffect(() => {
    if (!warningListActionRequest) {
      return;
    }

    dispatch(warningsFiltersActions.clearWarningListActionRequest({ panelId }));

    const { warning } = warningListActionRequest;
    const handlers = warningListActionHandlersRef.current;
    switch (warningListActionRequest.type) {
      case 'edit':
        void handlers.editWarning(warning);
        break;
      case 'delete':
        handlers.deleteWarning(warning);
        break;
      case 'deleteReview':
        handlers.deleteReviewWarning(warning);
        break;
      case 'expire':
        handlers.expireWarning(warning);
        break;
      case 'withdraw':
        handlers.withdrawWarning(warning);
        break;
      case 'aviation':
        handlers.handleAviationProductClick(
          warning as SigmetWarning | AirmetWarning,
          warningListActionRequest.actionType as ProductAction,
        );
        break;
      case 'maritime':
        handlers.handleMaritimeProductClick(
          warning as MaritimeWarning,
          warningListActionRequest.actionType as MaritimeWarningAction,
        );
        break;
      default:
        break;
    }
  }, [dispatch, panelId, warningListActionRequest]);

  const onRemoveSuccessHooks = (warningId: string): void => {
    setConfirmDialogOptions(undefined);
    // Close warning dialog if needed
    if (selectedWarningId === warningId) {
      dispatch(
        uiActions.setToggleOpenDialog({
          setOpen: false,
          type: publicWarningDialogType,
        }),
      );
    }
  };

  // TODO remove when all is moved to tanstack
  const onRemoveSuccess = (
    warningId: string,
    snackbarMessage?: string,
  ): void => {
    // close confirm dialog
    setConfirmDialogOptions(undefined);
    // Close warning dialog if needed
    if (selectedWarningId === warningId) {
      dispatch(
        uiActions.setToggleOpenDialog({
          setOpen: false,
          type: publicWarningDialogType,
        }),
      );
    }

    if (snackbarMessage) {
      openSnackbar(snackbarMessage);
    }
    refetchWarnings();
  };

  const onUpdateFilters = React.useCallback(
    (filterState: Partial<FilterState>, systemUpdate = false): void => {
      dispatch(
        warningsFiltersActions.setWarningFilters({
          filterState,
          panelId,
          systemUpdate,
        }),
      );
    },
    [dispatch, panelId],
  );

  const filterHookState = useWarningFilters(
    combinedWarnings,
    onUpdateFilters,
    savedFilterState,
    presetFilterState,
  );

  const filteredWarnings = React.useMemo(
    () => combinedWarnings.filter(filterHookState.byFilterState),
    [combinedWarnings, filterHookState.byFilterState],
  );

  const isOverLimit = filteredWarnings.length > MAX_WARNINGS_DISPLAY;
  const hasCustomTimeFilter =
    savedFilterState.time.mode === TimeFilterMode.custom;
  const shouldShowAlertBanner = hasCustomTimeFilter && isOverLimit;

  const displayedWarnings = React.useMemo(
    () => (shouldShowAlertBanner ? [] : filteredWarnings),
    [shouldShowAlertBanner, filteredWarnings],
  );

  const toggleWarningForBatchDelete = React.useCallback(
    (warningId: string): void => {
      const toggleIds = getBatchDeleteToggleIds(warningId, displayedWarnings);
      setSelectedForBatchDelete((prev) =>
        prev.includes(warningId)
          ? prev.filter((id) => !toggleIds.includes(id))
          : Array.from(new Set([...prev, ...toggleIds])),
      );
    },
    [displayedWarnings],
  );

  React.useEffect(() => {
    setSelectedForBatchDelete((prev) => {
      const visibleIds = new Set(
        displayedWarnings
          .map((warning) => warning.id)
          .filter((id): id is string => !!id),
      );
      const next = prev.filter((id) => visibleIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [displayedWarnings]);

  const expandedSections = useSelector((store: WarningModuleStore) =>
    getExpandedSectionsByPanelId(store, panelId),
  );

  const setExpandedSections = React.useCallback(
    (nextExpandedSections: Record<string, boolean>): void => {
      dispatch(
        warningsFiltersActions.setExpandedSectionsForPanel({
          panelId,
          expandedSections:
            nextExpandedSections as unknown as ExpandedWarningSections,
          systemUpdate: false,
        }),
      );
    },
    [dispatch, panelId],
  );

  React.useEffect(() => {
    if (!presetExpandedSections) {
      return;
    }

    dispatch(
      warningsFiltersActions.setExpandedSectionsForPanel({
        panelId,
        expandedSections: presetExpandedSections,
        systemUpdate: true,
      }),
    );
  }, [dispatch, panelId, presetExpandedSections]);

  const filteredWarningsByExpandedSections = React.useMemo(
    () =>
      displayedWarnings.filter((warning) => {
        const sectionKey = warning.status || warning.productStatus;
        return sectionKey ? Boolean(expandedSections[sectionKey]) : false;
      }),
    [displayedWarnings, expandedSections],
  );

  const { handleHoverWarning, hoveredFeatureId } = useWarningListFeatureSync({
    filteredWarningsByExpandedSections,
    hiddenWarningIds,
    panelId,
    selectedFormWarning,
    selectedWarningId,
  });

  const doBatchDelete = async (warningIds: string[]): Promise<string[]> => {
    const warningsById = new Map(
      displayedWarnings.map((warning) => [warning.id, warning]),
    );

    // One event delete removes every warning of that event
    const eventWarningIds = new Map<string, string[]>();
    warningIds.forEach((warningId) => {
      const warning = warningsById.get(warningId);
      if (warning?.type === 'warningsEvent' && warning.eventId) {
        const ids = eventWarningIds.get(warning.eventId) ?? [];
        eventWarningIds.set(warning.eventId, [...ids, warningId]);
      }
    });

    const promises: {
      ids: string[];
      promise: Promise<SigmetFromBackend | AirmetFromBackend | Headers | null>;
    }[] = Array.from(eventWarningIds, ([eventId, ids]) => ({
      ids,
      promise: deleteWarningEvent({ eventId }),
    }));

    promises.push(
      ...warningIds.flatMap((warningId) => {
        const warning = warningsById.get(warningId);

        if (!warning || warning.type === 'warningsEvent') {
          return [];
        }

        if (warning.type === 'public') {
          const promise =
            warning.status === PublicWarningStatus.TODO
              ? saveWarning({
                  id: warning.id!,
                  data: {
                    ...warning,
                    status: PublicWarningStatus.IGNORED,
                    type: 'public',
                  },
                })
              : deletePublicWarning({ id: warningId });

          return [
            {
              ids: [warningId],
              promise: promise as Promise<
                SigmetFromBackend | AirmetFromBackend | Headers | null
              >,
            },
          ];
        }

        if (warning.type === 'airmet' || warning.type === 'sigmet') {
          return [
            {
              ids: [warningId],
              promise: postAviationProduct(
                warning.sigmetAirmetDetails as AviationProduct,
                'DISCARDED',
                warning.type,
              ) as Promise<
                SigmetFromBackend | AirmetFromBackend | Headers | null
              >,
            },
          ];
        }

        // Maritime warnings are ignored as they cannot have status DRAFT/TODO
        return [];
      }),
    );

    // Correct version
    const results = await Promise.allSettled(
      promises.map(async ({ promise }) => promise),
    );

    return promises
      .filter((_, index) => results[index].status === 'fulfilled')
      .flatMap(({ ids }) => ids);
  };

  const onClickBatchDelete = (warningIds: string[]): void => {
    // Filter out any warnings that are excluded because of the filter
    const warningIdSet = new Set(
      displayedWarnings.map((warning) => warning.id),
    );
    const filteredWarningIds = warningIds.filter((id) => warningIdSet.has(id));

    const isLocked = filteredWarningIds.some((warningId) => {
      const fullWarning = displayedWarnings.find(
        (warning) => warning.id === warningId,
      );
      const editor = fullWarning?.editor ?? '';
      const currentUser = auth?.username ?? '';

      return editor !== '' && editor !== currentUser;
    });

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-delete-all-confirm'),
      title: t('warning-dialog-delete-all-title'),
      description: t('warning-dialog-delete-all-description', {
        ns: WARN_NAMESPACE,
        NUMBER: filteredWarningIds.length,
      }),
      request: async () => doBatchDelete(filteredWarningIds),
      callback: (data?: unknown) => {
        const successfulIds = (data as string[]) || [];
        if (filteredWarningIds.includes(selectedWarningId)) {
          dispatch(
            uiActions.setToggleOpenDialog({
              setOpen: false,
              type: publicWarningDialogType,
            }),
          );
        }
        const isSelectedEventDeleted = displayedWarnings.some(
          (warning) =>
            warning.type === 'warningsEvent' &&
            warning.eventId === selectedEventId &&
            successfulIds.includes(warning.id!),
        );
        if (isSelectedEventDeleted) {
          dispatch(
            uiActions.setToggleOpenDialog({
              setOpen: false,
              type: warningEventDialogType,
            }),
          );
        }

        // Only show snackbar and remove from selection if there were successes
        if (successfulIds.length > 0) {
          openSnackbar(t('warning-dialog-delete-all-succes'));
          setSelectedForBatchDelete((prev) =>
            prev.filter((id) => !successfulIds.includes(id)),
          );
        }

        const failedCount = filteredWarningIds.length - successfulIds.length;
        if (failedCount > 0) {
          throw new Error(
            t('warning-dialog-delete-all-failed', { NUMBER: failedCount }),
          );
        } else {
          setConfirmDialogOptions(undefined);
        }
        refetchWarnings();
      },
      isLocked,
    });
  };

  return isLoggedIn ? (
    <>
      <PublicWarningList
        error={error ?? undefined}
        expandedSections={expandedSections}
        filterHookState={filterHookState}
        hiddenWarningIds={hiddenWarningIds}
        hoveredFeatureId={hoveredFeatureId}
        isLoading={isFetching}
        lastUpdatedTime={lastUpdateTime}
        limitAlertCount={filteredWarnings.length}
        onActionAviationWarning={handleAviationProductClick}
        onActionMaritimeWarning={handleMaritimeProductClick}
        onClearBatchDeleteSelection={clearBatchDeleteSelection}
        onClickBatchDelete={onClickBatchDelete}
        onCreateWarning={createWarning}
        onDeleteReviewWarning={deleteReviewWarning}
        onDeleteWarning={deleteWarning}
        onEditWarning={editWarning}
        onExpireWarning={expireWarning}
        onHoverWarning={handleHoverWarning}
        onRefetchWarnings={refetchWarnings}
        onSelectAllForBatchDelete={selectAllForBatchDelete}
        onSelectWarning={selectWarning}
        onToggleWarningVisibility={handleToggleWarningVisibility}
        onWithdrawWarning={withdrawWarning}
        selectedEventId={selectedEventId}
        selectedForBatchDelete={selectedForBatchDelete}
        selectedWarningId={selectedWarningId}
        setExpandedSections={setExpandedSections}
        shouldDisableAviationAmendOption={
          aviationConfig?.disable_amend_action === true
        }
        showLimitAlert={shouldShowAlertBanner}
        toggleWarningForBatchDelete={toggleWarningForBatchDelete}
        warnings={displayedWarnings}
      />
      {confirmDialogOptions ? (
        <ConfirmDeleteWarningDialog
          callback={confirmDialogOptions.callback}
          confirmLabel={confirmDialogOptions.confirmLabel}
          description={confirmDialogOptions.description}
          isLocked={confirmDialogOptions.isLocked}
          onClose={(): void => setConfirmDialogOptions(undefined)}
          request={confirmDialogOptions.request}
          title={confirmDialogOptions.title}
        />
      ) : null}
      {!!cancelCtx && (
        <CancelAviationConfirmDialog
          aviationConfig={aviationConfig as SigmetConfig}
          cancelAviationProduct={async (product, t, opts) =>
            cancelAviationProduct(product, t, opts)
          }
          confirmLabel={getConfirmationDialogButtonLabel(
            t,
            ProductAction.CANCEL,
            cancelCtx.type,
          )}
          onClose={closeCancelDialog}
          open={cancelOpen}
          product={cancelCtx.product}
          showVA={cancelCtx.showVA}
          sigmet={cancelCtx.sigmet as Sigmet}
          title={getConfirmationDialogTitle(
            t,
            ProductAction.CANCEL,
            cancelCtx.type,
          )}
          type={cancelCtx.type}
        />
      )}
    </>
  ) : (
    <AlertBanner
      dataTestId="public-warning-list-auth-error"
      severity="error"
      title={t('error-auth-login')}
    />
  );
};
